// src/admin/BannerManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { uploadToImageKit } from '../utils/imagekitUpload';
import {
  Plus, Edit, Trash2, X, Upload, Image as ImageIcon, Save,
  Palette, Type
} from 'lucide-react';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('content'); // 'content' | 'style'

  const defaultFormData = {
    // Content
    title: '',
    description: '',
    buttonText: '',
    buttonLink: '',
    image: '',
    order: 1,
    active: true,

    // Text Styling
    titleColor: '#ffffff',
    titleSize: 'xl',           // sm, md, lg, xl, 2xl, 3xl
    titleWeight: 'bold',       // normal, semibold, bold
    descriptionColor: '#ffffff',
    descriptionSize: 'md',     // sm, md, lg
    textAlign: 'left',         // left, center, right
    textPosition: 'center',    // top, center, bottom

    // Overlay
    overlayColor: '#000000',
    overlayOpacity: 40,        // 0-100

    // Button Styling
    buttonBgColor: '#15803d',
    buttonTextColor: '#ffffff',
    buttonSize: 'md',          // sm, md, lg
    buttonRounded: 'lg',       // none, md, lg, full
  };

  const [formData, setFormData] = useState(defaultFormData);

  // ================= FETCH BANNERS =================
  const fetchBanners = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'banners'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.order || 99) - (b.order || 99));
      setBanners(data);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // ================= IMAGE UPLOAD =================
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMessage({ type: '', text: '' });
    try {
      const url = await uploadToImageKit(file, 'banners');
      setFormData({ ...formData, image: url });
      setMessage({ type: 'success', text: 'Image uploaded successfully!' });
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: 'Image upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  // ================= SAVE =================
  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.image) {
      setMessage({ type: 'error', text: 'Please upload a banner image.' });
      return;
    }

    const bannerData = {
      title: formData.title || '',
      description: formData.description || '',
      buttonText: formData.buttonText || '',
      buttonLink: formData.buttonLink || '',
      image: formData.image,
      order: Number(formData.order) || 1,
      active: formData.active === true,

      // Styling
      titleColor: formData.titleColor || '#ffffff',
      titleSize: formData.titleSize || 'xl',
      titleWeight: formData.titleWeight || 'bold',
      descriptionColor: formData.descriptionColor || '#ffffff',
      descriptionSize: formData.descriptionSize || 'md',
      textAlign: formData.textAlign || 'left',
      textPosition: formData.textPosition || 'center',

      // Overlay
      overlayColor: formData.overlayColor || '#000000',
      overlayOpacity: Number(formData.overlayOpacity) || 0,

      // Button
      buttonBgColor: formData.buttonBgColor || '#15803d',
      buttonTextColor: formData.buttonTextColor || '#ffffff',
      buttonSize: formData.buttonSize || 'md',
      buttonRounded: formData.buttonRounded || 'lg',
    };

    try {
      if (editingBanner) {
        await updateDoc(doc(db, 'banners', editingBanner.id), bannerData);
        setMessage({ type: 'success', text: 'Banner updated!' });
      } else {
        const newId = Date.now().toString();
        await setDoc(doc(db, 'banners', newId), {
          ...bannerData,
          createdAt: new Date(),
        });
        setMessage({ type: 'success', text: 'Banner created!' });
      }

      setTimeout(() => {
        setShowModal(false);
        setEditingBanner(null);
        setFormData(defaultFormData);
        setActiveTab('content');
        fetchBanners();
      }, 1000);
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save banner.' });
    }
  };

  // ================= DELETE =================
  const handleDelete = async (banner) => {
    if (!window.confirm(`Delete banner "${banner.title || 'Untitled'}"?`)) return;
    try {
      await deleteDoc(doc(db, 'banners', banner.id));
      fetchBanners();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete banner.');
    }
  };

  // ================= MODALS =================
  const openAddModal = () => {
    setEditingBanner(null);
    setFormData(defaultFormData);
    setActiveTab('content');
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      description: banner.description || '',
      buttonText: banner.buttonText || '',
      buttonLink: banner.buttonLink || '',
      image: banner.image || '',
      order: banner.order || 1,
      active: banner.active !== false,

      titleColor: banner.titleColor || '#ffffff',
      titleSize: banner.titleSize || 'xl',
      titleWeight: banner.titleWeight || 'bold',
      descriptionColor: banner.descriptionColor || '#ffffff',
      descriptionSize: banner.descriptionSize || 'md',
      textAlign: banner.textAlign || 'left',
      textPosition: banner.textPosition || 'center',

      overlayColor: banner.overlayColor || '#000000',
      overlayOpacity: banner.overlayOpacity !== undefined ? banner.overlayOpacity : 40,

      buttonBgColor: banner.buttonBgColor || '#15803d',
      buttonTextColor: banner.buttonTextColor || '#ffffff',
      buttonSize: banner.buttonSize || 'md',
      buttonRounded: banner.buttonRounded || 'lg',
    });
    setActiveTab('content');
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const toggleActive = async (banner) => {
    try {
      await updateDoc(doc(db, 'banners', banner.id), {
        active: banner.active === true ? false : true,
      });
      fetchBanners();
    } catch (error) {
      console.error('Toggle error:', error);
    }
  };

  // ================= PREVIEW HELPERS =================
  const getTitleSizeClass = (size) => {
    const map = {
      sm: 'text-lg md:text-2xl',
      md: 'text-xl md:text-3xl',
      lg: 'text-2xl md:text-4xl',
      xl: 'text-3xl md:text-5xl',
      '2xl': 'text-4xl md:text-6xl',
      '3xl': 'text-5xl md:text-7xl',
    };
    return map[size] || map.xl;
  };

  const getDescSizeClass = (size) => {
    const map = {
      sm: 'text-xs md:text-sm',
      md: 'text-sm md:text-base',
      lg: 'text-base md:text-lg',
      xl: 'text-lg md:text-xl',
    };
    return map[size] || map.md;
  };

  const getBtnSizeClass = (size) => {
    const map = {
      sm: 'px-4 py-2 text-xs md:text-sm',
      md: 'px-6 py-3 text-sm md:text-base',
      lg: 'px-8 py-4 text-base md:text-lg',
    };
    return map[size] || map.md;
  };

  const getBtnRoundedClass = (r) => {
    const map = { none: 'rounded-none', md: 'rounded-md', lg: 'rounded-lg', full: 'rounded-full' };
    return map[r] || map.lg;
  };

  const getAlignmentClass = (align) => {
    const map = { left: 'items-start text-left', center: 'items-center text-center', right: 'items-end text-right' };
    return map[align] || map.left;
  };

  const getPositionClass = (pos) => {
    const map = { top: 'items-start pt-12', center: 'items-center', bottom: 'items-end pb-12' };
    return map[pos] || map.center;
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Banner Management</h1>
          <p className="text-gray-500 text-sm mt-1">Website ke homepage banners yahan se manage karein.</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-green-700 text-white px-5 py-3 rounded-lg hover:bg-green-800 transition flex items-center gap-2 font-semibold shadow-md"
        >
          <Plus size={18} /> Add Banner
        </button>
      </div>

      {/* Banners Grid */}
      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading banners...</p>
        </div>
      ) : banners.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <ImageIcon size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No banners yet. Add your first banner!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {banners.map((banner) => (
            <div key={banner.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
              <div className="relative h-40 bg-gray-100">
                <img src={banner.image} alt={banner.title} className="w-full h-full object-cover" />
                <button
                  onClick={() => toggleActive(banner)}
                  className={`absolute top-2 right-2 text-xs font-semibold px-2 py-1 rounded-full ${
                    banner.active !== false ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'
                  }`}
                >
                  {banner.active !== false ? 'Active' : 'Inactive'}
                </button>
                <span className="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full">
                  Order: {banner.order || 1}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-800 line-clamp-1">{banner.title || 'Untitled Banner'}</h3>
                <p className="text-sm text-gray-500 line-clamp-2 mt-1">{banner.description || 'No description'}</p>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => openEditModal(banner)}
                    className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                  >
                    <Edit size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(banner)}
                    className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 py-2 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">

            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-800">
                {editingBanner ? 'Edit Banner' : 'Add New Banner'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 px-5 sticky top-[68px] bg-white z-10">
              <button
                type="button"
                onClick={() => setActiveTab('content')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition ${
                  activeTab === 'content'
                    ? 'border-green-700 text-green-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Type size={16} /> Content
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('style')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition ${
                  activeTab === 'style'
                    ? 'border-green-700 text-green-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Palette size={16} /> Style & Colors
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">

              {message.text && (
                <div className={`text-sm p-3 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {message.text}
                </div>
              )}

              {/* ================= CONTENT TAB ================= */}
              {activeTab === 'content' && (
                <>
                  {/* Image */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Banner Image *</label>
                    {formData.image ? (
                      <div className="relative">
                        <img src={formData.image} alt="Banner preview" className="w-full h-48 object-cover rounded-lg" />
                        <label className="absolute bottom-2 right-2 bg-white text-gray-700 px-3 py-1 rounded-lg shadow-md text-xs font-semibold cursor-pointer hover:bg-gray-50">
                          Change Image
                          <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                        </label>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition">
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
                            <p className="mt-3 text-sm text-gray-600">Uploading...</p>
                          </>
                        ) : (
                          <>
                            <Upload className="text-gray-400 mb-2" size={32} />
                            <p className="text-sm font-semibold text-gray-600">Click to upload image</p>
                            <p className="text-xs text-gray-400 mt-1">PNG, JPG (Max 5 MB)</p>
                          </>
                        )}
                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                      </label>
                    )}
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                      placeholder="e.g., HI GUYS"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none"
                      placeholder="e.g., HOW ARE YOU"
                    />
                  </div>

                  {/* Button */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Button Text</label>
                      <input
                        type="text"
                        value={formData.buttonText}
                        onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                        placeholder="e.g., Shop Now"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Button Link</label>
                      <input
                        type="text"
                        value={formData.buttonLink}
                        onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                        placeholder="e.g., /shop"
                      />
                    </div>
                  </div>

                  {/* Order + Active */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Display Order</label>
                      <input
                        type="number"
                        value={formData.order}
                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 1 })}
                        className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                        min="1"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.active}
                          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                          className="w-5 h-5 text-green-700"
                        />
                        <span className="text-sm font-semibold text-gray-700">Show on Website</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* ================= STYLE TAB ================= */}
              {activeTab === 'style' && (
                <>
                  {/* Text Alignment + Position */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Text Alignment</label>
                      <select
                        value={formData.textAlign}
                        onChange={(e) => setFormData({ ...formData, textAlign: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm bg-white"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Text Position (Vertical)</label>
                      <select
                        value={formData.textPosition}
                        onChange={(e) => setFormData({ ...formData, textPosition: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm bg-white"
                      >
                        <option value="top">Top</option>
                        <option value="center">Center</option>
                        <option value="bottom">Bottom</option>
                      </select>
                    </div>
                  </div>

                  {/* Title Styling */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h3 className="text-sm font-bold text-gray-800">Title Styling</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Color</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.titleColor}
                            onChange={(e) => setFormData({ ...formData, titleColor: e.target.value })}
                            className="w-12 h-10 rounded border cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.titleColor}
                            onChange={(e) => setFormData({ ...formData, titleColor: e.target.value })}
                            className="flex-1 border border-gray-300 rounded-lg py-2 px-3 text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Size</label>
                        <select
                          value={formData.titleSize}
                          onChange={(e) => setFormData({ ...formData, titleSize: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white"
                        >
                          <option value="sm">Small</option>
                          <option value="md">Medium</option>
                          <option value="lg">Large</option>
                          <option value="xl">Extra Large</option>
                          <option value="2xl">2X Large</option>
                          <option value="3xl">3X Large</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Weight</label>
                        <select
                          value={formData.titleWeight}
                          onChange={(e) => setFormData({ ...formData, titleWeight: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white"
                        >
                          <option value="normal">Normal</option>
                          <option value="semibold">Semi Bold</option>
                          <option value="bold">Bold</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Description Styling */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h3 className="text-sm font-bold text-gray-800">Description Styling</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Color</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.descriptionColor}
                            onChange={(e) => setFormData({ ...formData, descriptionColor: e.target.value })}
                            className="w-12 h-10 rounded border cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.descriptionColor}
                            onChange={(e) => setFormData({ ...formData, descriptionColor: e.target.value })}
                            className="flex-1 border border-gray-300 rounded-lg py-2 px-3 text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Size</label>
                        <select
                          value={formData.descriptionSize}
                          onChange={(e) => setFormData({ ...formData, descriptionSize: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white"
                        >
                          <option value="sm">Small</option>
                          <option value="md">Medium</option>
                          <option value="lg">Large</option>
                          <option value="xl">Extra Large</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Overlay */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h3 className="text-sm font-bold text-gray-800">Background Overlay</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Overlay Color</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.overlayColor}
                            onChange={(e) => setFormData({ ...formData, overlayColor: e.target.value })}
                            className="w-12 h-10 rounded border cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.overlayColor}
                            onChange={(e) => setFormData({ ...formData, overlayColor: e.target.value })}
                            className="flex-1 border border-gray-300 rounded-lg py-2 px-3 text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          Overlay Opacity: {formData.overlayOpacity}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={formData.overlayOpacity}
                          onChange={(e) => setFormData({ ...formData, overlayOpacity: parseInt(e.target.value) })}
                          className="w-full accent-green-700"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Button Styling */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h3 className="text-sm font-bold text-gray-800">Button Styling</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">BG Color</label>
                        <input
                          type="color"
                          value={formData.buttonBgColor}
                          onChange={(e) => setFormData({ ...formData, buttonBgColor: e.target.value })}
                          className="w-full h-10 rounded border cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Text Color</label>
                        <input
                          type="color"
                          value={formData.buttonTextColor}
                          onChange={(e) => setFormData({ ...formData, buttonTextColor: e.target.value })}
                          className="w-full h-10 rounded border cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Size</label>
                        <select
                          value={formData.buttonSize}
                          onChange={(e) => setFormData({ ...formData, buttonSize: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg py-2 px-2 text-sm bg-white"
                        >
                          <option value="sm">Small</option>
                          <option value="md">Medium</option>
                          <option value="lg">Large</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Rounded</label>
                        <select
                          value={formData.buttonRounded}
                          onChange={(e) => setFormData({ ...formData, buttonRounded: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg py-2 px-2 text-sm bg-white"
                        >
                          <option value="none">None</option>
                          <option value="md">Small</option>
                          <option value="lg">Medium</option>
                          <option value="full">Full</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* LIVE PREVIEW */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <h3 className="text-sm font-bold text-gray-800 mb-3">Live Preview</h3>
                    <div className="relative w-full h-48 md:h-64 rounded-lg overflow-hidden bg-gray-800">
                      {formData.image && (
                        <img
                          src={formData.image}
                          alt="Preview"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundColor: formData.overlayColor,
                          opacity: formData.overlayOpacity / 100,
                        }}
                      ></div>
                      <div
                        className={`absolute inset-0 flex p-6 ${getPositionClass(formData.textPosition)}`}
                      >
                        <div className={`flex flex-col max-w-md ${getAlignmentClass(formData.textAlign)}`}>
                          {formData.title && (
                            <h2
                              className={`${getTitleSizeClass(formData.titleSize)} ${formData.titleWeight} mb-2 leading-tight`}
                              style={{ color: formData.titleColor }}
                            >
                              {formData.title}
                            </h2>
                          )}
                          {formData.description && (
                            <p
                              className={`${getDescSizeClass(formData.descriptionSize)} mb-3`}
                              style={{ color: formData.descriptionColor }}
                            >
                              {formData.description}
                            </p>
                          )}
                          {formData.buttonText && (
                            <span
                              className={`inline-block font-semibold ${getBtnSizeClass(formData.buttonSize)} ${getBtnRoundedClass(formData.buttonRounded)}`}
                              style={{
                                backgroundColor: formData.buttonBgColor,
                                color: formData.buttonTextColor,
                              }}
                            >
                              {formData.buttonText}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Submit */}
              <div className="flex gap-3 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition flex items-center justify-center gap-2 ${
                    uploading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  <Save size={18} />
                  {editingBanner ? 'Update Banner' : 'Create Banner'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default BannerManagement;